import { useState } from "react";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Button, useNotify, useRefresh } from "react-admin";
import dataProvider from "server/dataProvider";
import { dormitoryRequests } from "api/dormitoryApi";
import { useNotifyError } from "utils/useNotifyError";
import { currentRoomLabel, hasPendingMove, pendingRoomLabel } from "../Partials/roomLabels";

// Physical arrival confirmation — the second, separate step of a transfer. The destination was
// approved earlier through the allocation flow; this records that the resident actually moved.
// ConfirmModal disables its buttons while the request is in flight (no repeated submissions) and
// surfaces backend errors via notifyError while keeping the previous display, so onConfirm needs
// no try/catch of its own.
//
// The list row it was rendered from can be older than the move it describes: a cancelled campaign
// drops its transfers out of the accommodation's pendingMove fields, so a row fetched beforehand
// still shows one. The accommodation is therefore read again on open and the move resolved from that
// response - a read only, which completes and cancels nothing by itself.
const ConfirmMoveDialog = ({ record }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  const [accommodation, setAccommodation] = useState(null);

  const handleOpen = async () => {
    if (!record?.id) {
      notify("Липсва идентификатор на настаняването.", { type: "error" });
      return false;
    }

    try {
      const { data } = await dataProvider.customRequest(
        dormitoryRequests.getAccommodationById(record.id),
        null,
        "GET",
      );

      if (!hasPendingMove(data)) {
        notify("Няма предстоящо преместване за потвърждаване.", { type: "warning" });
        refresh();
        return false;
      }

      setAccommodation(data);
    } catch (err) {
      notifyError(err);
      return false;
    }

    return true;
  };

  const handleConfirm = async () => {
    await dataProvider.customRequest(dormitoryRequests.confirmAccommodationMove(record.id), null, "PUT");
    notify("Преместването е потвърдено", { type: "success" });
    refresh();
  };

  // The freshly read accommodation drives the wording, so the rooms named are the ones being moved
  // between right now, in the same "current → destination" form the list uses.
  const move = accommodation || record;

  return (
    <ConfirmModal
      onOpen={handleOpen}
      onConfirm={handleConfirm}
      titleContent="Потвърждаване на преместване"
      saveTitle="Потвърди преместване"
      closeTitle="Отказ"
      resetProps={() => setAccommodation(null)}
      message={`Потвърждавате, че студентът се е преместил от стая ${currentRoomLabel(move)} в стая ${pendingRoomLabel(move)}?`}
      triggerButton={<Button startIcon={<CheckCircleIcon />} label="Потвърди преместване" />}
    />
  );
};

export default ConfirmMoveDialog;
